/**
 * Range Slider Component
 * Handles dual-range slider functionality for price filters
 */

const rangeSlider = (filter) => {
  return {
    filter,
    minValue: 0,
    maxValue: 1000,
    
    /**
     * Initialize the range slider
     */
    init() {
      // Set initial values from filter
      this.minValue = this.getFilterRangeMin();
      this.maxValue = this.getFilterRangeMax();
      
      // Apply current filter values if any
      this.syncWithAppliedFilters();
    },

    /**
     * Get the minimum possible value for this filter
     */
    getFilterRangeMin() {
      if (this.filter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(this.filter.values[0].input);
          return input.price?.min || 0;
        } catch (e) {
          return 0;
        }
      }
      return 0;
    },

    /**
     * Get the maximum possible value for this filter
     */
    getFilterRangeMax() {
      if (this.filter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(this.filter.values[0].input);
          return input.price?.max || 1000;
        } catch (e) {
          return 1000;
        }
      }
      return 1000;
    },

    /**
     * Sync with any applied filters
     */
    syncWithAppliedFilters() {
      const appliedFilter = this.$store.search.appliedFilters?.find(f => f.id === this.filter.id);
      if (appliedFilter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(appliedFilter.values[0].input);
          if (input.price) {
            this.minValue = input.price.min;
            this.maxValue = input.price.max;
          }
        } catch (e) {
          // Keep default values
        }
      }
    },

    /**
     * Update minimum value
     */
    updateMin(value) {
      const newMin = Math.max(this.getFilterRangeMin(), Math.min(parseFloat(value), this.maxValue));
      this.minValue = newMin;
    },

    /**
     * Update maximum value
     */
    updateMax(value) {
      const newMax = Math.min(this.getFilterRangeMax(), Math.max(parseFloat(value), this.minValue));
      this.maxValue = newMax;
    },

    /**
     * Get the current range as a percentage for styling
     */
    get rangePercentage() {
      const total = this.getFilterRangeMax() - this.getFilterRangeMin();
      const currentMin = ((this.minValue - this.getFilterRangeMin()) / total) * 100;
      const currentMax = ((this.maxValue - this.getFilterRangeMin()) / total) * 100;
      
      return {
        min: Math.max(0, Math.min(100, currentMin)),
        max: Math.max(0, Math.min(100, currentMax)),
        width: Math.max(0, currentMax - currentMin)
      };
    },

    /**
     * Check if current values differ from defaults
     */
    get hasActiveRange() {
      return this.minValue !== this.getFilterRangeMin() || 
             this.maxValue !== this.getFilterRangeMax();
    },

    /**
     * Format currency value
     */
    formatCurrency(value) {
      const currency = window.Shopify?.currency?.active || '$';
      return `${currency}${parseFloat(value).toFixed(2)}`;
    },

    /**
     * Get formatted display text
     */
    get displayText() {
      return `${this.formatCurrency(this.minValue)} - ${this.formatCurrency(this.maxValue)}`;
    }
  };
};

// Export for Alpine.js auto-loading
export default {
  name: 'rangeSlider',
  component: rangeSlider
};